import type { ConditionHistoryPoint } from '@axilon-apps/domains-ontology-ui-state'

import { cn } from '../../lib/utils'

interface TriggerSparklineProps {
  history: ConditionHistoryPoint[]
  threshold?: number
  fired: boolean
  width?: number
  height?: number
  className?: string
}

/**
 * Sparkline tuned for the RCA trigger row: bound axis to include the
 * threshold so the trace's distance from trip is legible, and dash the
 * threshold line in danger tone so it reads as "the line you don't want
 * to cross." Color the trace red when fired, brand when armed.
 */
export function TriggerSparkline({
  history,
  threshold,
  fired,
  width = 140,
  height = 28,
  className
}: TriggerSparklineProps) {
  if (history.length < 2) {
    return (
      <div
        className={cn('flex items-center justify-center text-10 text-ink-4', className)}
        style={{ width, height }}
      >
        no readings
      </div>
    )
  }

  const padX = 2
  const padY = 3
  const values = history.map((p) => p.value)
  const ref =
    threshold !== undefined && Number.isFinite(threshold) ? [...values, threshold] : values
  const min = Math.min(...ref)
  const max = Math.max(...ref)
  const span = max - min || 1
  const xStep = (width - padX * 2) / Math.max(history.length - 1, 1)
  const yFor = (v: number) => height - padY - ((v - min) / span) * (height - padY * 2)

  const linePath = history
    .map((p, i) => {
      const x = padX + i * xStep
      const y = yFor(p.value)
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')

  const thresholdY = threshold !== undefined && Number.isFinite(threshold) ? yFor(threshold) : null

  const traceClass = fired ? 'stroke-st-down' : 'stroke-brand'

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="value over time"
      className={cn('block', className)}
    >
      {thresholdY !== null && (
        <line
          x1={padX}
          x2={width - padX}
          y1={thresholdY}
          y2={thresholdY}
          className="stroke-st-down/40"
          strokeWidth={1}
          strokeDasharray="3 2"
        />
      )}
      <path
        d={linePath}
        className={traceClass}
        vectorEffect="non-scaling-stroke"
        fill="none"
        strokeWidth={1.4}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  )
}
